import { Navigate } from "react-router-dom";
import { ShieldOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAdminAuthStore } from "@/store/admin-auth-store";
import { can, hasRoleAtLeast } from "@/lib/rbac";
import type { Action, Role } from "@/types/admin-rbac";

interface RoleGuardProps {
  /** Yêu cầu tối thiểu role này (đi cùng tier rank). */
  minRole?: Role;
  /** Hoặc yêu cầu một action cụ thể trong RBAC matrix. */
  action?: Action;
  /** Fallback khi không đủ quyền — mặc định: trang "Forbidden". */
  fallback?: React.ReactNode;
  /** Nếu set, redirect thay vì render fallback. */
  redirectTo?: string;
  children: React.ReactNode;
}

/**
 * Chặn route/section theo role hoặc action. Đặt bên trong AdminGuard
 * (AdminGuard lo authenticated, RoleGuard lo authorized).
 */
export default function RoleGuard({
  minRole,
  action,
  fallback,
  redirectTo,
  children,
}: RoleGuardProps) {
  const { t } = useTranslation("admin");
  const admin = useAdminAuthStore((s) => s.admin);
  const role = admin?.role ?? null;

  const allowed =
    (!minRole || hasRoleAtLeast(role, minRole)) &&
    (!action || can(role, action));

  if (allowed) return <>{children}</>;
  if (redirectTo) return <Navigate to={redirectTo} replace />;

  return (
    <>
      {fallback ?? (
        <div className="flex h-full min-h-[60vh] flex-col items-center justify-center gap-3 p-8 text-center">
          <ShieldOff className="h-10 w-10 text-amber-500" />
          <h2 className="text-base font-semibold text-t0">
            {t("roleGuard.title")}
          </h2>
          <p className="max-w-sm text-xs text-t2">
            {t("roleGuard.descPrefix")}{" "}
            <span className="font-mono text-t1">{role ?? "?"}</span>
            {t("roleGuard.descSuffix")}
          </p>
        </div>
      )}
    </>
  );
}
